/**
 * ExternalReviewersPanel
 * ──────────────────────
 * Feb 27, 2026 — Client-chart admin section for granting / revoking
 * External Reviewer read-only access on a per-client basis.
 *
 * Visible to: admins, supervisors, billing managers.
 * Backed by:
 *   GET    /api/clients/{client_id}/external-reviewers
 *   POST   /api/clients/{client_id}/external-reviewers       body={user_id}
 *   DELETE /api/clients/{client_id}/external-reviewers/{user_id}
 *
 * The dropdown is populated from the global user list filtered to
 * `is_external_reviewer=true` — so creating a new External Reviewer
 * user automatically makes them available here.
 */

import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus, X, Eye, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ExternalReviewersPanel = ({ clientId, users = [], canManage = false }) => {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingUserId, setPendingUserId] = useState('');

  // Only `is_external_reviewer=true` users are eligible.
  const eligibleReviewers = useMemo(
    () => (users || []).filter((u) => u?.is_external_reviewer && !u?.is_archived),
    [users],
  );

  // Eligible reviewers that don't already have an active grant.
  const grantedIds = useMemo(() => new Set(grants.map((g) => g.user_id)), [grants]);
  const availableReviewers = useMemo(
    () => eligibleReviewers.filter((u) => !grantedIds.has(u.id)),
    [eligibleReviewers, grantedIds],
  );

  const fetchGrants = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/clients/${clientId}/external-reviewers`);
      setGrants(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      if (e.response?.status !== 403) {
        // 403 just means current user can't see this section — quiet.
        toast.error(e.response?.data?.detail || 'Failed to load external reviewers');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGrants();
  }, [clientId]);

  const handleGrant = async () => {
    if (!pendingUserId) {
      toast.error('Pick an External Reviewer to grant');
      return;
    }
    try {
      await axios.post(`${API}/clients/${clientId}/external-reviewers`, { user_id: pendingUserId });
      toast.success('Access granted');
      setPendingUserId('');
      fetchGrants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to grant access');
    }
  };

  const handleRevoke = async (userId, userLabel) => {
    if (!window.confirm(`Revoke External Reviewer access for ${userLabel}?`)) return;
    try {
      await axios.delete(`${API}/clients/${clientId}/external-reviewers/${userId}`);
      toast.success('Access revoked');
      fetchGrants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke');
    }
  };

  // Quietly hide if the requester isn't allowed to manage — keeps
  // the chart clean for non-admins.  Eligible reviewers list will
  // also be empty for them anyway because we'd 403 the fetch.
  if (!canManage) return null;

  return (
    <div className="border border-amber-200 bg-amber-50/40 rounded-lg p-4" data-testid="external-reviewers-panel">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="h-4 w-4 text-amber-700" />
        <h3 className="text-sm font-semibold text-amber-900">External Reviewers</h3>
      </div>
      <p className="text-xs text-amber-800 mb-3">
        Grant read-only chart access to outside auditors / utilization reviewers.  Reviewers see ONLY clients explicitly granted here.
      </p>

      {/* Current grants */}
      {loading && <p className="text-xs text-gray-500 italic">Loading…</p>}
      {!loading && grants.length === 0 && (
        <p className="text-xs text-gray-500 italic mb-3">No external reviewers have access to this chart.</p>
      )}
      {!loading && grants.length > 0 && (
        <ul className="space-y-2 mb-3">
          {grants.map((g) => (
            <li
              key={g.id}
              className="flex items-center justify-between bg-white border border-amber-100 rounded px-3 py-2"
              data-testid={`external-reviewer-row-${g.user_id}`}
            >
              <div className="text-sm">
                <div className="font-medium text-gray-800 flex items-center gap-1.5">
                  <Eye className="h-3.5 w-3.5 text-gray-400" />
                  {g.user_full_name || g.user_email_snapshot || 'Unknown reviewer'}
                  {g.user_is_archived && (
                    <span className="text-xs text-red-600">(archived)</span>
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  {g.user_email || g.user_email_snapshot} • Granted {new Date(g.granted_at).toLocaleDateString()}
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                title="Revoke access"
                onClick={() => handleRevoke(g.user_id, g.user_full_name || g.user_email_snapshot || 'this reviewer')}
                data-testid={`external-reviewer-revoke-${g.user_id}`}
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {/* Grant new */}
      {availableReviewers.length === 0 ? (
        <p className="text-xs text-gray-500 italic">
          No more eligible External Reviewers to grant.  Create one in User Management with the "External Reviewer" flag enabled.
        </p>
      ) : (
        <div className="flex items-center gap-2">
          <select
            value={pendingUserId}
            onChange={(e) => setPendingUserId(e.target.value)}
            className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 bg-white"
            data-testid="external-reviewer-select"
          >
            <option value="">Pick an external reviewer…</option>
            {availableReviewers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.full_name} ({u.email})
              </option>
            ))}
          </select>
          <Button
            size="sm"
            className="bg-amber-600 hover:bg-amber-700 text-white text-xs"
            onClick={handleGrant}
            data-testid="external-reviewer-grant-btn"
            disabled={!pendingUserId}
          >
            <UserPlus className="h-3.5 w-3.5 mr-1" />
            Grant access
          </Button>
        </div>
      )}
    </div>
  );
};

export default ExternalReviewersPanel;
